import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

/** Semantic tone — separate from the brand accent; encodes state at a glance. */
export type PillTone = "info" | "success" | "warning" | "critical" | "neutral";

const TONE_PALETTE: Record<Exclude<PillTone, "neutral">, "info" | "success" | "warning" | "error"> = {
  info: "info",
  success: "success",
  warning: "warning",
  critical: "error",
};

/**
 * Maps a free-text status to a semantic tone. Central so every screen colours the same status
 * identically. Extend here rather than per-screen.
 */
export function statusTone(status: string): PillTone {
  const s = status.toLowerCase().replace(/[\s_-]+/g, "");
  if (["billed", "processed", "posted", "delivered", "approved", "completed", "active", "paid", "resulted"].includes(s)) return "success";
  if (["ready", "new", "open", "scheduled", "ordered", "collected", "received"].includes(s)) return "info";
  if (["processing", "inbilling", "intransit", "draft", "pending", "partial", "inprocess", "partiallyresulted"].includes(s)) return "warning";
  if (["critical", "cancelled", "canceled", "void", "failed", "rejected", "overdue", "inactive"].includes(s)) return "critical";
  return "neutral";
}

/** A soft-tinted pill with a leading state dot — the "catch it at a glance" status marker. */
export function StatusPill({ label, tone }: { label: string; tone?: PillTone }) {
  const theme = useTheme();
  const resolved = tone ?? statusTone(label);
  const dark = theme.palette.mode === "dark";

  const color =
    resolved === "neutral"
      ? theme.palette.text.secondary
      : (dark ? theme.palette[TONE_PALETTE[resolved]].light : theme.palette[TONE_PALETTE[resolved]].dark);
  const base =
    resolved === "neutral" ? theme.palette.text.secondary : theme.palette[TONE_PALETTE[resolved]].main;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: 5,
        fontSize: "0.72rem",
        fontWeight: 600,
        lineHeight: 1.6,
        color,
        backgroundColor: alpha(base, dark ? 0.2 : 0.12),
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} />
      {label}
    </Box>
  );
}
